// src/lib/activation.js
import { problemIds } from './entitySchema';

// The activation path. Order is a suggestion; completion is derived from records.
export const MILESTONES = [
  { id: 'anchor', label: 'Set your anchor', stage: 'Observe', hint: 'Name the outcome you are accountable for this quarter.', show: { route: '/okrs', anchor: 'objective-add' } },
  { id: 'problem', label: 'Name a problem', stage: 'Understand', hint: 'Write down one customer problem, in their words.', show: { route: '/discovery', anchor: 'problem-add' } },
  { id: 'evidence', label: 'Make it real', stage: 'Decide', hint: 'Attach one quote, ticket or number to the problem.', show: { route: '/discovery', anchor: 'evidence-add' } },
  { id: 'commit', label: 'Commit', stage: 'Execute', hint: 'Turn a validated problem into an initiative.', show: { route: '/discovery', anchor: 'initiative-handoff' } },
  { id: 'aim', label: 'Aim it', stage: 'Measure', hint: 'Link the initiative to your goal, or give it a success metric.', show: { route: '/roadmap', anchor: 'initiative-okr' } },
  { id: 'act', label: 'Act on an insight', stage: 'Learn', hint: 'Open the top item in your Morning Brief and act on it.', show: { route: '/', anchor: 'brief-first' } },
];
export const HABITS = [
  { id: 'return', label: 'Come back tomorrow', hint: 'Your brief changes overnight.' },
  { id: 'decide', label: 'Record a decision', hint: 'Capture a call you made, with a date to review it.', show: { route: '/decisions', anchor: 'decision-add' } },
  { id: 'power', label: 'Power up', optional: true, hint: 'Connect Jira or turn on Autopilot.', show: { route: '/onboarding' } },
  { id: 'loop', label: 'Close the loop', hint: 'Review a decision against what happened, or record a learning.', show: { route: '/decisions' } },
];

export const emptyActivation = () => ({
  started_at: null, legacy: false, path: null, goal_objective_id: null,
  sample_ids: [], sample_map: null, triggers: {}, visits: [], last_visit: null,
  previous_brief_ids: [], acted: [], celebrated: [], paused: false,
});
export function mergeActivation(current, patch) {
  const base = { ...emptyActivation(), ...(current || {}) };
  return { ...base, ...(patch || {}), triggers: { ...base.triggers, ...(patch?.triggers || {}) } };
}
export const applyPatch = (activation, patch) => mergeActivation(activation, typeof patch === 'function' ? patch(mergeActivation(activation)) : patch);

export function ownData(data, sampleIds = []) {
  const sample = new Set((sampleIds || []).map(String));
  return Object.fromEntries(Object.entries(data).map(([key, rows]) => [key, Array.isArray(rows) ? rows.filter(row => !sample.has(String(row.id))) : rows]));
}

export function activationState(data, activation) {
  const a = mergeActivation(activation);
  const own = ownData(data, a.sample_ids);
  const list = key => own[key] || [];
  const ownProblems = new Set(list('problems').map(p => String(p.id)));
  const objectives = new Set((data.objectives || []).map(o => String(o.id)));
  const done = {
    anchor: list('objectives').length > 0,
    problem: list('problems').length > 0,
    evidence: list('evidence').some(e => ownProblems.has(String(e.problem_card_id))),
    commit: list('initiatives').some(i => problemIds(i).length > 0),
    aim: list('initiatives').some(i => (i.okr_id && objectives.has(String(i.okr_id))) || Boolean(String(i.success_metric || '').trim())),
    act: a.acted.length > 0,
    return: new Set(a.visits).size >= 2,
    decide: list('decisions').some(d => d.review_date),
    // Autopilot's source scout always writes source records, so imports cover both.
    power: list('imports').length > 0,
    loop: list('decisions').some(d => d.status === 'reviewed') || list('learnings').length > 0,
  };
  const milestones = MILESTONES.map(m => ({ ...m, done: done[m.id] }));
  const habits = HABITS.map(h => ({ ...h, done: done[h.id] }));
  const completed = milestones.filter(m => m.done).length;
  return {
    milestones, habits, completed, activated: completed === MILESTONES.length,
    next: milestones.find(m => !m.done) || habits.find(h => !h.done && !h.optional) || null,
    doneIds: [...milestones, ...habits].filter(x => x.done).map(x => x.id),
  };
}

export const dayKey = date => { const d = new Date(date); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
export function dayNumber(startedAt, now) {
  if (!startedAt) return 0;
  const a = new Date(startedAt), b = new Date(now);
  return Math.round((new Date(b.getFullYear(), b.getMonth(), b.getDate()) - new Date(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000);
}

// Records today's brief; on the first load of a new date the last visit's brief becomes the comparison baseline.
export function visitPatch(activation, now, briefIds) {
  const a = mergeActivation(activation);
  const today = dayKey(now);
  const visits = a.visits.includes(today) ? a.visits : [...a.visits, today].slice(-30);
  const sameIds = a.last_visit && a.last_visit.brief_ids.join('|') === briefIds.join('|');
  if (a.last_visit?.date === today && sameIds && visits === a.visits) return null;
  const rolled = a.last_visit && a.last_visit.date !== today ? { previous_brief_ids: a.last_visit.brief_ids } : {};
  return { ...rolled, visits, last_visit: { date: today, brief_ids: [...briefIds] } };
}
